import { useContext, useState, useEffect } from 'react';
import { UpdateCompanyClientData } from '@esg/syntiro-api-core';
import { MetadataError } from '@ep/error-handling';
import { Drawer, Grid, Theme, Tooltip, useTheme } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { ReactComponent as EditIcon } from '../../../assets/icons/Edit.svg';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { subscription_label } from '../../../lib/app/subscription';
import DataGridLoading from '../../shared/datagrid/DataGridLoading';
import DataGridNoRows from '../../shared/datagrid/DataGridNoRows';
import DataGridToolbar from '../../shared/datagrid/DataGridToolbar';
import { CompanyExtended } from '../../../lib/app/company';
import {
  COMPONENT_UNKNOWN_FAILURE,
  FEEDBACK_ACTION_FAILURE,
  FEEDBACK_UNKNOWN_FAILURE,
  FUNCTION_UNKNOWN_FAILURE
} from '../../../util/global_variables';
import { getCompanies, updateCompany } from '../../../graphql/services/company.service';
import ConfigEntityForm, { ConfigEntityFormType, ConfigEntityInput } from '../ConfigEntityForm';

const SOURCE = 'PanelSubscriptions.tsx';

export type CompanyGcpLinkGridRow = {
  id: string;
  group_id: string;
  name: string;
  client_bigquery_dataset: string;
  client_bigquery_dataset_sync: boolean;
};

// Input schema types.
interface CompanyGcpLinkInputData {
  [key: string]: CompanyGcpLinkInputData[keyof CompanyGcpLinkInputData];
  client_bigquery_dataset: string;
  client_bigquery_dataset_sync: boolean;
}

/**
 * Datagrid handling the display of company bigquery links and configuration.
 * @returns {JSX.Element}
 */
const PanelGCPLinks = () => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const [actionRow, setActionRow] = useState<CompanyGcpLinkGridRow | null>(null);
  const [companyGcpLinkRows, setCompanyGcpLinkRows] = useState<Array<CompanyGcpLinkGridRow>>([]);
  const [configEntityFormType, setConfigEntityFormType] = useState<ConfigEntityFormType | null>(
    null
  );
  const [gcpLinkInputData, setGcpLinkInputData] = useState<CompanyGcpLinkInputData | null>(null);
  const [gridLoading, setGridLoading] = useState<boolean>(true);

  const theme: Theme = useTheme();

  // Input schema declarations with required node factories.
  const gcp_link_inputs: Array<ConfigEntityInput<CompanyGcpLinkInputData>> = [
    {
      id: 'client_bigquery_dataset',
      type: 'text',
      label: 'Client BigQuery Dataset',
      ignore_banned_characters: true,
      onControlledInputChange: (input_value: unknown) => {
        if (typeof input_value === 'string' && gcpLinkInputData)
          setGcpLinkInputData({ ...gcpLinkInputData, client_bigquery_dataset: input_value });
      }
    },
    {
      id: 'client_bigquery_dataset_sync',
      type: 'boolean',
      label: 'Enable Dataset Sync',
      onControlledInputChange: (input_value: unknown) => {
        if (typeof input_value === 'boolean' && gcpLinkInputData)
          setGcpLinkInputData({ ...gcpLinkInputData, client_bigquery_dataset_sync: input_value });
      }
    }
  ];

  // Handler functions
  const handleEditClick = (row: CompanyGcpLinkGridRow): void => {
    const gcp_link_input_data: CompanyGcpLinkInputData = {
      client_bigquery_dataset: row.client_bigquery_dataset,
      client_bigquery_dataset_sync: row.client_bigquery_dataset_sync
    };
    setGcpLinkInputData(gcp_link_input_data);
    setActionRow(row);
    setConfigEntityFormType('edit');
  };

  const handleCloseConfigForm = (): void => {
    setConfigEntityFormType(null);
  };

  const handleUpdateRows = (rows_data: Array<CompanyGcpLinkGridRow>): void => {
    setCompanyGcpLinkRows(rows_data);
  };

  const columns: Array<GridColDef> = [
    {
      field: 'name',
      headerName: 'Company',
      headerAlign: 'left',
      align: 'left',
      flex: 1
    },
    {
      field: 'client_bigquery_dataset',
      headerName: 'Client BigQuery Dataset',
      headerAlign: 'left',
      align: 'left',
      flex: 1
    },
    {
      field: 'client_bigquery_dataset_sync',
      headerName: 'Client BigQuery Sync',
      headerAlign: 'left',
      type: 'boolean',
      align: 'left',
      flex: 1
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      headerAlign: 'right',
      align: 'right',
      hideable: false,
      flex: 1,
      getActions: ({ row }) => {
        return [
          <>
            <GridActionsCellItem
              key={1}
              icon={
                <Tooltip title="Edit subscription">
                  <EditIcon width={25} fill={theme.palette.primary.main} />
                </Tooltip>
              }
              size="large"
              label="Edit"
              sx={{
                color: 'primary.main'
              }}
              onClick={() => handleEditClick(row)}
              disabled={row.locked}
            />
          </>
        ];
      }
    }
  ];

  // Row functions
  const fetchRows = async (): Promise<void> => {
    setGridLoading(true);
    try {
      // Load rows from database.
      const companies: Array<CompanyExtended> = await getCompanies();
      const company_gcp_link_rows: Array<CompanyGcpLinkGridRow> = companies.map(
        ({
          id,
          name,
          client_bigquery_dataset,
          client_bigquery_dataset_sync,
          group: { id: group_id }
        }: CompanyExtended) => ({
          id,
          group_id,
          name,
          client_bigquery_dataset,
          client_bigquery_dataset_sync
        })
      );
      // Load rows to memory.
      setCompanyGcpLinkRows(company_gcp_link_rows);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'fetchRows'),
          {},
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to fetch company gcp links', tracking_id),
        state: true,
        type: 'error'
      });
      return;
    } finally {
      setGridLoading(false);
    }
  };

  useEffect(() => {
    try {
      (async () => {
        setCompanyGcpLinkRows([]);
        fetchRows();
      })().catch((error) => {
        throw new Error(error);
      });
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : COMPONENT_UNKNOWN_FAILURE(SOURCE),
          null,
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_UNKNOWN_FAILURE(tracking_id),
        state: true,
        type: 'error'
      });
    }
    return;
  }, []);

  const mapGcpLinkInputToRowData = (
    input_data: CompanyGcpLinkInputData
  ): CompanyGcpLinkGridRow | undefined => {
    try {
      if (!actionRow) {
        throw new Error('Missing selected row for update');
      }
      return {
        id: actionRow.id,
        group_id: actionRow.group_id,
        name: actionRow.name,
        client_bigquery_dataset: input_data.client_bigquery_dataset,
        client_bigquery_dataset_sync: input_data.client_bigquery_dataset_sync
      };
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'mapGcpLinkInputToRowData'),
          {
            actionRow: actionRow,
            input_data: input_data
          },
          tracking_id
        )
      );
    }
  };

  const updateRow = async (
    updated_row: CompanyGcpLinkGridRow,
    original_data: CompanyGcpLinkGridRow
  ): Promise<void> => {
    try {
      const update_company_client_data: UpdateCompanyClientData = {
        id: updated_row.id,
        client_bigquery_dataset: updated_row.client_bigquery_dataset,
        client_bigquery_dataset_sync: updated_row.client_bigquery_dataset_sync
      };
      await updateCompany(
        {
          group_id: updated_row.group_id
        },
        update_company_client_data
      ).then(async () => {
        handleUpdateRows(
          companyGcpLinkRows.map((row: CompanyGcpLinkGridRow) =>
            updated_row.id === row.id ? { ...updated_row } : row
          )
        );
      });
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'updateRow'),
          {
            updated_row: updated_row,
            original_data: original_data
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to update company', tracking_id),
        state: true,
        type: 'error'
      });
    }
  };

  return (
    <>
      {/* Side Widget Panel */}
      <Drawer anchor={'right'} open={!!configEntityFormType} onClose={handleCloseConfigForm}>
        {gcpLinkInputData && (
          <ConfigEntityForm<CompanyGcpLinkInputData>
            form_title={`Edit GCP Link for ${actionRow?.name ?? 'Company'}`}
            form_icon={<EditIcon width={40} fill={theme.palette.primary.main} />}
            inputs={gcp_link_inputs}
            controlled_entity={gcpLinkInputData}
            handleClose={handleCloseConfigForm}
            handleConfirmInput={async (input_data: CompanyGcpLinkInputData) => {
              const updated_row_data: CompanyGcpLinkGridRow | undefined =
                mapGcpLinkInputToRowData(input_data);
              actionRow && updated_row_data && (await updateRow(updated_row_data, actionRow));
            }}
          />
        )}
      </Drawer>

      <Grid container spacing={8}>
        {/* Interactive Data Table */}
        <Grid size={12}>
          <DataGrid
            autoHeight
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10, page: 0 }
              },
              sorting: {
                sortModel: [{ field: 'modified', sort: 'desc' }]
              }
            }}
            hideFooter={companyGcpLinkRows.length > 1 ? false : true}
            columnHeaderHeight={companyGcpLinkRows.length < 1 ? 0 : 56}
            pageSizeOptions={[10, 25, 50, 100]}
            loading={gridLoading}
            rows={companyGcpLinkRows}
            columns={columns}
            disableRowSelectionOnClick
            slots={{
              toolbar: () => (
                <DataGridToolbar
                  disable_export={companyGcpLinkRows.length === 0}
                  entity_label={subscription_label}
                />
              ),
              noRowsOverlay: () => <DataGridNoRows missing_label={subscription_label} />,
              loadingOverlay: () => <DataGridLoading columns={columns.length} />
            }}
            sx={{ '&, [class^=MuiDataGrid]': { border: 'none' }, width: '100%' }}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default PanelGCPLinks;
